import { Email, ExpandLess, ExpandMore } from "@mui/icons-material";
import {
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { formatDisplayDate } from "utils/dateFormat";
import React, { useMemo, useState } from "react";
import { getAllIntakeFormResponses } from "utils/clientIntakeFormService";

const STATUS_COLORS = {
  PENDING: "warning",
  COMPLETED: "success",
  EXPIRED: "error",
};

const ResponseRow = ({ record }) => {
  const [expanded, setExpanded] = useState(false);

  const fields = useMemo(() => {
    try {
      return JSON.parse(record.fieldsJson || "[]");
    } catch {
      return [];
    }
  }, [record.fieldsJson]);

  const fieldMap = useMemo(() => Object.fromEntries(fields.map((f) => [f.id, f.label])), [fields]);

  return (
    <div className="border border-app-border rounded-lg overflow-hidden">
      <div
        className="flex items-center justify-between px-4 py-3 bg-app-surface hover:bg-app-surface-hover cursor-pointer"
        onClick={() => setExpanded((p) => !p)}
      >
        <div className="flex items-center gap-3 flex-wrap">
          <Chip
            label={record.tokenStatus}
            color={STATUS_COLORS[record.tokenStatus] || "default"}
            size="small"
          />
          {record.clientName && (
            <span className="text-sm font-semibold text-app-text">{record.clientName}</span>
          )}
          {record.clientEmail && (
            <span className="text-sm text-app-text-muted">{record.clientEmail}</span>
          )}
          <span className="text-sm text-app-text-muted">
            Sent: {formatDisplayDate(record.sentAt)}
          </span>
          {record.submittedAt && (
            <span className="text-sm text-app-text-muted">
              Submitted: {formatDisplayDate(record.submittedAt)}
            </span>
          )}
        </div>
        <IconButton size="small">{expanded ? <ExpandLess /> : <ExpandMore />}</IconButton>
      </div>
      <Collapse in={expanded}>
        <div className="px-4 py-4 bg-app-surface-muted border-t border-app-border">
          {record.tokenStatus === "PENDING" ? (
            <p className="text-sm text-app-text-muted italic">Awaiting client submission.</p>
          ) : record.responses ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {Object.entries(record.responses).map(([key, value]) => (
                <div key={key}>
                  <p className="text-xs font-semibold text-app-text-muted uppercase tracking-wide">
                    {fieldMap[key] || key}
                  </p>
                  <p className="text-sm text-app-text mt-0.5">
                    {Array.isArray(value) ? value.join(", ") : String(value || "—")}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-app-text-muted">No responses recorded.</p>
          )}
        </div>
      </Collapse>
    </div>
  );
};

const IntakeForms = () => {
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [search, setSearch] = useState("");

  const {
    data: records = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["allIntakeFormResponses"],
    queryFn: getAllIntakeFormResponses,
  });

  const filtered = useMemo(() => {
    return records.filter((r) => {
      const matchStatus = statusFilter === "ALL" || r.tokenStatus === statusFilter;
      const q = search.toLowerCase();
      const matchSearch =
        !q ||
        (r.clientName || "").toLowerCase().includes(q) ||
        (r.clientEmail || "").toLowerCase().includes(q);
      return matchStatus && matchSearch;
    });
  }, [records, statusFilter, search]);

  return (
    <div className="flex flex-wrap mt-4 dashBody">
      <div className="w-full px-4">
        <div className="relative flex flex-col min-w-0 break-words w-full shadow-lg rounded bg-app-surface text-app-text">
          <div className="rounded-t mb-0 px-6 py-4 border-b border-app-border">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h3 className="font-semibold text-lg text-app-text">
                  Intake Forms
                  {records.length > 0 && (
                    <span className="ml-2 text-sm font-normal text-app-text-muted">
                      ({filtered.length} of {records.length})
                    </span>
                  )}
                </h3>
                <p className="text-sm text-app-text-muted mt-0.5">
                  All intake forms sent to clients
                </p>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <TextField
                  size="small"
                  placeholder="Search by name or email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{ minWidth: 220 }}
                />
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="ALL">All</MenuItem>
                    <MenuItem value="PENDING">Pending</MenuItem>
                    <MenuItem value="COMPLETED">Completed</MenuItem>
                    <MenuItem value="EXPIRED">Expired</MenuItem>
                  </Select>
                </FormControl>
                <button
                  onClick={() => refetch()}
                  className="px-3 py-1.5 text-sm border border-app-border rounded-lg hover:bg-app-surface-muted text-app-text-secondary transition-colors"
                >
                  Refresh
                </button>
              </div>
            </div>
          </div>

          <div className="p-6">
            {isLoading ? (
              <div className="flex justify-center py-12">
                <CircularProgress />
              </div>
            ) : filtered.length === 0 ? (
              <Paper elevation={0} className="border border-app-border rounded-xl p-10 text-center">
                <Email className="text-slate-300 mb-3" style={{ fontSize: 40 }} />
                <p className="text-app-text-muted">No intake forms found.</p>
                <p className="text-app-text-muted text-xs mt-1">
                  Send intake forms from a client's profile.
                </p>
              </Paper>
            ) : (
              <div className="space-y-2">
                {filtered.map((record, idx) => (
                  <ResponseRow key={record.tokenId ?? record.id ?? idx} record={record} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntakeForms;
